import { Avatar } from 'module/ui/components/atoms/avatar';
import { HumanisedDate } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { DraggableTable } from 'module/ui/components/molecules/draggable-table';
import { TableBody } from 'module/ui/components/molecules/draggable-table/styles';
import { ColumnType } from 'module/ui/components/molecules/draggable-table/types';
import {
  faTrash,
  faEdit,
  faGripDotsVertical,
  faEye,
} from '@fortawesome/pro-regular-svg-icons';
import { PageTemplateLite } from 'api/page-template/types';
import { PageType } from 'api/page-template/types';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';

function hasPageTriggerCondition(condition: unknown): boolean {
  if (condition == null || typeof condition !== 'object') {
    return false;
  }

  const c = condition as {
    nativeFields?: unknown[];
    customFields?: unknown[];
    nestedFilter?: unknown[];
    aiFields?: unknown[];
  };

  return (
    (c.nativeFields?.length ?? 0) > 0 ||
    (c.customFields?.length ?? 0) > 0 ||
    (c.nestedFilter?.length ?? 0) > 0 ||
    (c.aiFields?.length ?? 0) > 0
  );
}

const StyledDraggableTable = styled.div`
  display: flex;
  flex-direction: column;

  ${TableBody} {
    max-height: calc(85vh - 150px);
    overflow-y: auto;
  }
`;

const LoadMoreFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: var(--spacing-04) 0;
  min-height: 48px;
`;

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
  pointer-events: none;
`;

const TemplateName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
`;

const SourceType = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
`;

const LastUpdatedBy = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
`;

const LastUpdatedOn = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 75px;
`;

export type TemplateTablePermissions = {
  canRead?: boolean;
  canWrite?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
};

export type TemplateTableProps = {
  templates: PageTemplateLite[];
  onStatusChange: (id: string) => void;
  pageType?: PageType;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  templatePermissions?: TemplateTablePermissions;
  isModuleFed?: boolean;
  onReorder?: (
    templates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => void;
  loading?: boolean;
  /** True while the next infinite-scroll page is being fetched. */
  loadingMore?: boolean;
  /** True when another page of templates can be loaded. */
  hasMore?: boolean;
  /** Called when the load-more sentinel intersects the table scroll root. */
  onLoadMore?: () => void;
};

export function TemplateTable({
  templates,
  onStatusChange,
  onEdit,
  onDelete,
  templatePermissions,
  isModuleFed = true,
  onReorder,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  pageType,
}: TemplateTableProps) {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const fullAccess = {
    canRead: true,
    canWrite: true,
    canCreate: true,
    canDelete: true,
  };
  const perms = !isModuleFed ? fullAccess : (templatePermissions ?? fullAccess);
  const canRead = perms.canRead !== false;
  const canWrite = perms.canWrite !== false;
  const canDelete = perms.canDelete !== false;

  useEffect(() => {
    if (!hasMore || !onLoadMore) {
      return;
    }

    const root = tableWrapperRef.current?.querySelector<HTMLElement>(
      '[data-draggable-table-body="true"]',
    );
    const sentinel = loadMoreSentinelRef.current;

    if (!root || !sentinel) {
      return;
    }

    const LOAD_MORE_THRESHOLD_PX = 48;

    const isNearBottom = () => {
      // Guard against false IO hits when the sentinel looks visible while the
      // user is still near the top of the scroll container.
      const remaining = root.scrollHeight - root.scrollTop - root.clientHeight;

      return remaining <= LOAD_MORE_THRESHOLD_PX;
    };

    const tryLoadMore = () => {
      if (loadingMore || !isNearBottom()) {
        return;
      }

      onLoadMore();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          tryLoadMore();
        }
      },
      {
        root,
        rootMargin: '0px',
        threshold: 0,
      },
    );

    observer.observe(sentinel);
    // Scroll backup: if the sentinel was already intersecting, IO won't re-fire
    // when the user later reaches the real bottom.
    root.addEventListener('scroll', tryLoadMore, { passive: true });

    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', tryLoadMore);
    };
  }, [hasMore, loadingMore, onLoadMore, templates.length]);

  const columns: ColumnType<PageTemplateLite>[] = [
    {
      key: 'drag',
      title: '',
      width: '24px',
      render: () =>
        pageType !== PageType.Email && (
          <div className="drag-handle">
            <FontAwesomeIcon
              icon={faGripDotsVertical}
              style={{ color: 'var(--gray-50)', cursor: 'grab' }}
            />
          </div>
        ),
    },
    {
      key: 'templateName',
      title: <FormattedMessage id="template_name" />,
      render: (value) => <TemplateName>{value}</TemplateName>,
    },
    {
      key: 'sourceType',
      title: <FormattedMessage id="use_for" />,
      render: (_, record) => (
        <SourceType>
          {hasPageTriggerCondition(record?.filters?.pageTriggerCondition)
            ? pageType === PageType.Email
              ? 'Specific Emails'
              : 'Specific Meetings'
            : pageType === PageType.Email
              ? 'All Emails'
              : 'All Meetings'}
        </SourceType>
      ),
    },
    {
      key: 'lastUpdatedBy',
      title: <FormattedMessage id="last_updated_by" />,
      render: (user) =>
        user ? (
          <FlexWrapperWithShortGap>
            <Avatar
              name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
              size={20}
              src={user.company?.companyLogoUrl}
            />
            <LastUpdatedBy>
              {user.firstName} {user.lastName}
            </LastUpdatedBy>
          </FlexWrapperWithShortGap>
        ) : null,
    },
    {
      key: 'updatedAt',
      title: <FormattedMessage id="last_updated_on" />,
      render: (date) => (
        <LastUpdatedOn>
          <HumanisedDate timestamp={moment(date)} />
        </LastUpdatedOn>
      ),
    },
    {
      key: 'createdAt',
      title: <FormattedMessage id="created_on" />,
      render: (date) => (
        <LastUpdatedOn>
          <HumanisedDate timestamp={moment(date)} />
        </LastUpdatedOn>
      ),
    },
    {
      key: 'createdBy',
      title: <FormattedMessage id="created_by" />,
      render: (user) =>
        user ? (
          <FlexWrapperWithShortGap>
            <Avatar
              name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
              size={20}
              src={user.company?.companyLogoUrl}
            />
            <LastUpdatedBy>
              {user.firstName} {user.lastName}
            </LastUpdatedBy>
          </FlexWrapperWithShortGap>
        ) : null,
    },

    {
      key: 'enabled',
      title: <FormattedMessage id="status" />,
      render: (value, record) => (
        <Switch
          checked={value}
          onChange={() => onStatusChange(String(record.templateId))}
          size="small"
          disabled={!canWrite}
        />
      ),
    },
    {
      key: 'actions',
      width: '56px',
      title: '',
      render: (_, record) => {
        const dropdownChildren: Array<{
          key: string;
          label: React.ReactNode;
          icon: React.ReactNode;
          danger?: boolean;
        }> = [];

        if (canRead && !canWrite) {
          dropdownChildren.push({
            key: 'view',
            label: <FormattedMessage id="view" />,
            icon: <FontAwesomeIcon icon={faEye} />,
          });
        }

        if (canWrite) {
          dropdownChildren.push({
            key: 'edit',
            label: <FormattedMessage id="edit" />,
            icon: <FontAwesomeIcon icon={faEdit} />,
          });
        }

        if (canDelete) {
          dropdownChildren.push({
            key: 'delete',
            label: <FormattedMessage id="delete" />,
            icon: <FontAwesomeIcon icon={faTrash} />,
            danger: true,
          });
        }

        if (dropdownChildren.length === 0) {
          return null;
        }

        return (
          <MoreActionsDropdown
            menu={{
              onClick: ({ key }) => {
                if (key === 'view' || key === 'edit') {
                  onEdit(String(record.templateId));
                } else if (key === 'delete') {
                  onDelete(String(record.templateId));
                }
              },
              items: [
                {
                  type: 'group',
                  label: <FormattedMessage id="more_actions" />,
                  children: dropdownChildren,
                },
              ],
            }}
          />
        );
      },
    },
  ];

  const bodyFooter =
    hasMore || loadingMore ? (
      <>
        {hasMore ? (
          <LoadMoreSentinel
            ref={loadMoreSentinelRef}
            data-cy="template-table-load-more-sentinel"
            aria-hidden
          />
        ) : null}
        {loadingMore ? (
          <LoadMoreFooter data-cy="next-page-list-loader">
            <Loading size="small" />
          </LoadMoreFooter>
        ) : null}
      </>
    ) : null;

  return (
    <StyledDraggableTable ref={tableWrapperRef}>
      <DraggableTable<PageTemplateLite & { id: string }>
        columns={columns}
        data={templates.map((template) => ({
          ...template,
          id: String(template.templateId),
        }))}
        onDragEnd={
          onReorder && pageType !== PageType.Email
            ? (oldIndex, newIndex) => {
                const newTemplates = [...templates];
                const [movedItem] = newTemplates.splice(oldIndex, 1);

                newTemplates.splice(newIndex, 0, movedItem);
                onReorder(newTemplates, movedItem.templateId, newIndex);
              }
            : undefined
        }
        onRowClick={(record) => {
          if ((canRead || canWrite) && record?.templateId != null) {
            onEdit(String(record.templateId));
          }
        }}
        loading={loading}
        bodyFooter={bodyFooter}
      />
    </StyledDraggableTable>
  );
}
